import { track, addUserProperties } from '/app/utils/analytics';

// Signup happens on accounts.bignerve.com behind a full-page redirect, so the app
// infers "first login after signup" from two signals:
//   1. `?signup=1` on the return URL (set by accounts after registration), stashed
//      by the token middleware before it strips the param from the URL;
//   2. the account's creation timestamp, if /me/detailed exposes one, being recent.
// A per-user localStorage key keeps the event to one fire per user per browser.

const PENDING_KEY = 'bignerve:signup-pending';
const TRACKED_KEY = (userId) => `bignerve:signup-tracked:${userId}`;
const RECENT_SIGNUP_MS = 30 * 60 * 1000;

function safe(fn, fallback) {
    try {
        return fn();
    } catch {
        return fallback;
    }
}

export function markSignupPending() {
    safe(() => sessionStorage.setItem(PENDING_KEY, '1'));
}

function accountCreatedAt(user) {
    const raw = user?.created_at ?? user?.createdAt ?? user?.date_joined;
    const date = raw ? new Date(raw) : null;

    return date && !Number.isNaN(date.getTime()) ? date : null;
}

/**
 * Fire "Signup Completed" once for a freshly registered user. Call right after
 * `heap.identify`. Never throws.
 */
export function trackSignupIfNew(user) {
    safe(() => {
        if (typeof window === 'undefined' || !user?.id) {
            return;
        }

        const pending = safe(() => sessionStorage.getItem(PENDING_KEY) === '1', false);
        const createdAt = accountCreatedAt(user);
        const isRecent = !!createdAt && Date.now() - createdAt.getTime() < RECENT_SIGNUP_MS;

        if (!pending && !isRecent) {
            return;
        }

        safe(() => sessionStorage.removeItem(PENDING_KEY));

        if (safe(() => localStorage.getItem(TRACKED_KEY(user.id)), null)) {
            return;
        }

        const signupDate = (createdAt || new Date()).toISOString();

        track('Signup Completed', {
            detected_by: pending ? 'signup_param' : 'created_at',
        });
        addUserProperties({ signup_date: signupDate });

        safe(() => localStorage.setItem(TRACKED_KEY(user.id), signupDate));
    });
}
